import {
	endOfDay as dateEndOfDay,
	isValid,
	parse as dateParse,
	parseISO,
} from 'date-fns'
import { th } from 'date-fns/locale'
import { formatInTimeZone, toZonedTime } from 'date-fns-tz'

// Default timezone for all operations
const TIMEZONE = 'Asia/Bangkok'

/**
 * Get current date/time in Asia/Bangkok timezone
 */
export function now(): Date {
	return toZonedTime(new Date(), TIMEZONE)
}

/**
 * Format date with Thai locale and Asia/Bangkok timezone
 */
export function format(date: Date | string, pattern: string): string {
	const dateObj = typeof date === 'string' ? parseISO(date) : date
	return formatInTimeZone(dateObj, TIMEZONE, pattern, { locale: th })
}

/**
 * Parse ISO date string and convert to Asia/Bangkok timezone
 */
export function parse(dateString: string): Date {
	return toZonedTime(parseISO(dateString), TIMEZONE)
}

/**
 * Parse date string with custom format in Asia/Bangkok timezone
 */
export function parseWithFormat(dateString: string, formatPattern: string): Date {
	const parsed = dateParse(dateString, formatPattern, new Date())
	return toZonedTime(parsed, TIMEZONE)
}

/**
 * Get end of day for given date in Asia/Bangkok timezone
 */
export function endOfDay(date: Date | string): Date {
	const dateObj = typeof date === 'string' ? parse(date) : date
	const zonedDate = toZonedTime(dateObj, TIMEZONE)
	return dateEndOfDay(zonedDate)
}

/**
 * Get end of day timestamp for given date in Asia/Bangkok timezone
 */
export function endOfDayTimestamp(date: Date | string): number {
	return endOfDay(date).getTime()
}

/**
 * Get current date string in YYYY-MM-DD format (Asia/Bangkok)
 */
export function getCurrentDate(): string {
	return format(now(), 'yyyy-MM-dd')
}

/**
 * Get current datetime in ISO format (Asia/Bangkok)
 */
export function getCurrentDateTime(): string {
	return now().toISOString()
}

/**
 * Get current timestamp formatted for filenames (Asia/Bangkok)
 */
export function getCurrentTimestamp(): string {
	return format(now(), 'yyyy-MM-dd\'T\'HH-mm-ss-SSS\'Z\'')
}

/**
 * Get current timestamp in milliseconds (Asia/Bangkok)
 */
export function getTimestamp(): number {
	return now().getTime()
}

/**
 * Format date for display (DD/MM/YYYY format)
 */
export function formatForDisplay(date: Date | string): string {
	if (!date) return ''
	return format(date, 'dd/MM/yyyy')
}

/**
 * Format date with time for display (DD/MM/YYYY HH:mm format)
 */
export function formatDateTime(date: Date | string): string {
	if (!date) return ''
	return format(date, 'dd/MM/yyyy HH:mm')
}

/**
 * Check if date is valid
 */
export function isValidDate(date: any): boolean {
	if (date instanceof Date) {
		return isValid(date)
	}
	if (typeof date === 'string') {
		return isValid(parseISO(date))
	}
	return false
}

// Export configured timezone for reference
export { TIMEZONE }